import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for bidirectional transaction synchronization
 * Ensures changes in one section reflect in others
 */
export const useTransactionSync = () => {
  const [syncTrigger, setSyncTrigger] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Trigger a sync event
  const triggerSync = useCallback((source, data) => {
    const event = {
      source,
      data,
      timestamp: Date.now()
    };
    
    setLastUpdate(event);
    setSyncTrigger(prev => prev + 1);
    
    // Broadcast to other tabs/windows
    if (typeof window !== 'undefined') {
      localStorage.setItem('transaction_sync', JSON.stringify(event));
      window.dispatchEvent(new CustomEvent('transactionSync', { detail: event }));
    }
    
    console.log('🔄 Transaction sync triggered from:', source, data);
  }, []);

  // Listen for sync events from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'transaction_sync') {
        try {
          const event = JSON.parse(e.newValue);
          setLastUpdate(event);
          setSyncTrigger(prev => prev + 1);
        } catch (err) {
          console.error('Error parsing sync event:', err);
        }
      }
    };

    const handleCustomEvent = (e) => {
      setLastUpdate(e.detail);
      setSyncTrigger(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('transactionSync', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('transactionSync', handleCustomEvent);
    };
  }, []);

  return {
    syncTrigger,
    lastUpdate,
    triggerSync
  };
};

/**
 * Hook to sync balance changes across components
 */
export const useBalanceSync = (userId) => {
  const { syncTrigger, lastUpdate, triggerSync } = useTransactionSync();
  const [balance, setBalance] = useState(null);

  const updateBalance = useCallback(async (newBalance, source) => {
    setBalance(newBalance);
    triggerSync(source, { type: 'balance_update', userId, balance: newBalance });
  }, [userId, triggerSync]);

  // Listen for balance updates from other sources
  useEffect(() => {
    if (lastUpdate?.data?.type === 'balance_update' && lastUpdate.data.userId === userId) {
      setBalance(lastUpdate.data.balance);
    }
  }, [lastUpdate, userId]);

  return {
    balance,
    updateBalance,
    syncTrigger
  };
};

/**
 * Hook to sync transaction status across components
 */
export const useTransactionStatusSync = () => {
  const { syncTrigger, lastUpdate, triggerSync } = useTransactionSync();
  const [pendingCount, setPendingCount] = useState(0);

  const updateTransactionStatus = useCallback((transactionId, status, source) => {
    triggerSync(source, {
      type: 'transaction_status_change',
      transactionId,
      status
    });
  }, [triggerSync]);

  const updatePendingCount = useCallback((count) => {
    setPendingCount(count);
  }, []);

  // Listen for transaction status changes
  useEffect(() => {
    if (lastUpdate?.data?.type === 'transaction_status_change') {
      console.log('Transaction status changed:', lastUpdate.data);
    }
  }, [lastUpdate]);

  return {
    pendingCount,
    updatePendingCount,
    updateTransactionStatus,
    syncTrigger
  };
};
